import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileEncode from "filepond-plugin-file-encode";
import { errorMessage, formatDateTime } from "helpers/common";
import { api } from "helpers/axios";
import { toast } from "helpers/toast";
import ListPagination from "components/ui/ListPagination";

import "filepond/dist/filepond.min.css";
import { useOutletContext } from "react-router-dom";
import { helpTextIcon } from "helpers/helpText";
import ErrorAlert from "components/ui/ErrorAlert";
import { Modal } from "components/ui";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useTranslation } from "translation/useTranslation";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";

registerPlugin(FilePondPluginFileValidateType, FilePondPluginFileEncode);

export default function DataSettingImport({ tabsComponent }) {
  const { translate, editableTranslate, editableTranslateString } = useTranslation();

  const { helps } = useOutletContext();

  const dispatch = useDispatch();

  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [files, setFiles] = useState([]);

  const [dataSettingImport, setDataSettingImport] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    totalRows: 0,
    totalPages: 1,
    perPage: 20
  });

  const tableRef = useRef(null);
  const theadRef = useRef(null);

  const [resizedCount, setResizedCount] = useState(0);

  const dataSettingImportHelp = useMemo(() => {
    if (helps) {
      return helps.filter(h => h.key === 'data-setting-import-intro')[0] || null;
    }

    return null;
  }, [helps]);

  const fetchDataSettingImport = useCallback(async ({ silent = false, background = false, page = null } = {}) => {
    if (!page) page = pagination.page;

    try {
      if (!silent) setLoading(true);
      const { data, status } = await api.get(`/data-setting-import?limit=${pagination.perPage}&page=${page}`, { meta: { background } });
      if (status === 200) {
        setDataSettingImport(data.data);
        const temp = data.meta.pagination;
        setPagination({ page, totalRows: +temp.total, totalPages: temp.total_pages, perPage: +temp.per_page });
      }
    } catch (err) {
      setError(errorMessage(err, 'fetchDataSettingImport'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [pagination]);

  useEffect(() => {
    fetchDataSettingImport();
  }, [pagination.page, pagination.perPage]);

  const shouldPoll = useMemo(() => {
    return Array.isArray(dataSettingImport) && dataSettingImport.some((item) => ['pending', 'processing'].includes(item.status));
  }, [dataSettingImport]);

  useEffect(() => {
    if (!shouldPoll) return;

    const intervalId = setInterval(() => {
      fetchDataSettingImport({ silent: true, background: true });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [shouldPoll, fetchDataSettingImport]);

  useEffect(() => {
    setResizedCount(c => c + 1);

    const theadEl = theadRef.current;
    if (!theadEl) return;

    const handleScrollTable = () => {
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;

      const theadParentCoordinates = theadEl.parentElement.getBoundingClientRect();
      if (theadParentCoordinates.y < navHeight) {
        theadEl.style.transform = `translate3d(0, ${-theadParentCoordinates.y + navHeight}px, 0)`;
      } else {
        theadEl.style.transform = 'translate3d(0,0,0)';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, [dataSettingImport]);

  const handleCancel = () => {
    setFiles([]);
    setShowConfirm(false);
  };

  const handleClickConfirm = () => {
    setShowConfirm(false);
    handleSubmit(files[0]);
  };

  const handleSubmit = async (file) => {

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", file.file);

      const { status } = await api.post(
        "/data-setting-import/manual-upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (status === 200) {
        setFiles([]);
        setError("");

        toast.success(translate('form.success.upload_and_process_file'));

        fetchDataSettingImport();
      } else {
        setError("Error uploading file");
      }
    } catch (err) {
      setError(errorMessage(err, "upload file"));
    }

    setLoading(false);
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <div className="d-flex justify-content-between align-items-center border-bottom employee-management-tabs-div mb-4">
            {tabsComponent}
          </div>
          <div>
            <p className="section-description">
              {editableTranslate("global.menu.settings.data_setting_import_description")}
              {
                dataSettingImportHelp ? helpTextIcon(dataSettingImportHelp?.key, dataSettingImportHelp?.short_text) : ''
              }
            </p>
          </div>
          {error && (
            <div className="mt-3">
              <ErrorAlert error={error} onClose={() => setError(null)} />
            </div>
          )}

          <div className="mt-5">
            <div className="d-flex justify-content-center">
              <div className="col-md-6">
                <form>
                  <FilePond
                    name="file"
                    files={files}
                    acceptedFileTypes={["application/zip"]}
                    onupdatefiles={setFiles}
                    instantUpload={false}
                    allowFileEncode={true}
                    allowMultiple={false}
                    required={true}
                    credits={false}
                    storeAsFile={false}
                    onaddfile={(error, file) => {
                      if (error) {
                        setError(error);
                      } else {
                        setShowConfirm(true);
                      }
                    }}
                    labelIdle={editableTranslateString("user_import.drop_or_choose_file", "Dateien hier ablegen oder zum Hochladen anklicken")}
                  />
                </form>
              </div>
            </div>
          </div>

          <div className="mt-3">
            <div className="table-responsive has-custom-scrollbar">
              <table ref={tableRef} className="table common-table" data-testid="data-setting-import-table">
                <thead ref={theadRef} className="sticky-top">
                  <tr>
                    <th scope="col" className="">#</th>
                    <th scope="col" className="">{editableTranslate('data_setting_import.type')}</th>
                    <th scope="col" className="">{editableTranslate('data_setting_import.version')}</th>
                    <th scope="col" className="">{editableTranslate('data_setting_import.payload_filename')}</th>
                    <th scope="col" className="">{editableTranslate('data_setting_import.status')}</th>
                    <th scope="col" className="">{editableTranslate('data_setting_import.date')}</th>
                    <th scope="col" className="">{editableTranslate('data_setting_import.log')}</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    loading && <tr>
                      <td colSpan={7}>
                        <div className="d-flex justify-content-center" data-testid="loading">
                          <div className="spinner-border" role="status"><span className="sr-only">Wird geladen...</span></div>
                        </div>
                      </td>
                    </tr>
                  }
                  {!loading && Array.isArray(dataSettingImport) && dataSettingImport.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data', 'Keine Einträge')}</td>
                    </tr>
                  )}
                  {!loading && dataSettingImport && dataSettingImport.map((data, index) => (
                    <tr key={index} data-testid={`row-${data?.filename}`}>
                      <td>{((pagination?.page - 1) * pagination?.perPage) + index + 1}</td>
                      <td>{data.type}</td>
                      <td>{data.version}</td>
                      <td>
                        <span data-tooltip-id="data-setting-import-filename-tooltip" data-tooltip-content={translate('data_setting_import.payload_filename') + ': ' + data.payload_filename}>{data.filename}</span>
                      </td>
                      <td>
                        <span className={`badge ${data.status === 'success' ? 'badge-success' : (data.status === 'failed' ? 'badge-danger' : 'badge-info')}`}>
                          {editableTranslate('data_setting_import.status.' + data.status)}
                        </span>
                      </td>
                      <td>
                        {
                          data.processed_at && <div>{formatDateTime(data.processed_at)}</div>
                        }
                      </td>
                      <td>
                        {data.log && data.log.split(',').map((log, index) => (
                          <div key={index}>{log}</div>
                        ))}
                        {
                          !data.log && '-'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TableScrollbar tableRef={tableRef} data={dataSettingImport} trigger={resizedCount} />

            <ListPagination
              page={pagination.page}
              perPage={pagination.perPage}
              totalPages={pagination.totalPages}
              totalRows={pagination.totalRows}
              setPage={(page) => setPagination({ ...pagination, page })}
              setPerPage={(perPage) => setPagination({ ...pagination, page: 1, perPage })}
              className="mt-4"
            />
          </div>
        </div>
      </div>
      <Modal
        open={showConfirm}
        loading={loading}
        title={editableTranslate('form.dialog.confirmation_title', 'Sind Sie sicher?')}
        cancelLabel={editableTranslate('form.no')}
        okLabel={editableTranslate('form.yes')}
        onClose={handleCancel}
        onConfirm={handleClickConfirm}
        testIdPrefix="data-setting-import-confirm-"
      >
        {editableTranslate('form.dialog.confirmation')}
      </Modal>
      <ReactTooltip id="data-setting-import-filename-tooltip" place="bottom" />
    </>
  );
}
